import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ProposalBuilder } from '../../components/proposal/ProposalBuilder.js'
import { Badge, Button, Card, CardHeader, EmptyState, SkeletonRows } from '../../components/ui.js'
import { relativeTime } from '../../lib/format.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'

/**
 * The business's proposal templates.
 *
 * A template is a proposal with no customer: the words, the photos, the
 * packages and the lines, some of them left unpriced so they are priced for
 * each client when the template is used. Raising a proposal copies one and
 * nothing points back, so editing a template here never restates a proposal
 * already with a customer. Decision 80.
 *
 * "Remove" is retire, as everywhere in Settings: a retired template is off the
 * builder's list and still here to bring back.
 */
export function ProposalsPanel() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [showRetired, setShowRetired] = useState(false)
  // `null` is closed, `'new'` a new template, otherwise the template's id.
  const [editing, setEditing] = useState<string | null>(null)

  const templates = useQuery(trpc.proposalTemplates.list.queryOptions({ includeArchived: showRetired }))
  const archive = useMutation(
    trpc.proposalTemplates.archive.mutationOptions({
      onSuccess: () => void queryClient.invalidateQueries(trpc.proposalTemplates.pathFilter()),
    }),
  )

  return (
    <Card>
      <CardHeader
        title="Proposal templates"
        action={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => setShowRetired((current) => !current)}>
              {showRetired ? 'Hide retired' : 'Show retired'}
            </Button>
            <Button size="sm" variant="primary" onClick={() => setEditing('new')}>
              New template
            </Button>
          </div>
        }
      />

      <div className="px-[var(--row-pad-x)] py-3">
        <p className="mb-3 max-w-[560px] text-xs text-ink-500">
          Offered when somebody writes a proposal on a job — the job's own kind of work first. A line left
          without a price is priced for the client the proposal is for. A finished proposal can also be saved
          as a template from its builder.
        </p>

        {templates.isPending ? (
          <SkeletonRows rows={4} />
        ) : templates.error ? (
          <p className="text-[13px] text-red-700">{errorMessage(templates.error)}</p>
        ) : templates.data.length === 0 ? (
          <EmptyState
            title="No templates"
            description="Write the proposals the business sends most — an inspection, a barrier, a programme — once, here."
          />
        ) : (
          <ul className="flex flex-col divide-y divide-ink-100">
            {templates.data.map((template) => (
              <li key={template.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5">
                <button
                  type="button"
                  onClick={() => setEditing(template.id)}
                  className="flex min-w-0 flex-1 flex-col items-start text-left"
                >
                  <span className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold text-ink-900">{template.name}</span>
                    {template.archivedAt ? <Badge tone="neutral">Retired</Badge> : null}
                    {template.jobCategory ? (
                      <span className="text-[11px] text-ink-500">{template.jobCategory.name}</span>
                    ) : null}
                  </span>
                  <span className="text-xs text-ink-500">
                    {[
                      template.description,
                      template.optionNames.length > 0 ? template.optionNames.join(' · ') : null,
                      `${template.lineCount} line${template.lineCount === 1 ? '' : 's'}`,
                      `changed ${relativeTime(template.updatedAt)}`,
                    ]
                      .filter(Boolean)
                      .join(' — ')}
                  </span>
                </button>
                <Button size="sm" onClick={() => setEditing(template.id)}>
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  loading={archive.isPending && archive.variables?.id === template.id}
                  onClick={() => archive.mutate({ id: template.id, archived: !template.archivedAt })}
                >
                  {template.archivedAt ? 'Bring back' : 'Retire'}
                </Button>
              </li>
            ))}
          </ul>
        )}
        {archive.error ? <p className="mt-2 text-xs text-red-600">{errorMessage(archive.error)}</p> : null}
      </div>

      {editing ? (
        <ProposalBuilder
          kind="template"
          open
          templateId={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </Card>
  )
}
